import { useState } from 'react'

import {
  bucketRulesByStage,
  MAIN_STAGE_KEYS,
  OUTCOME_STAGE_KEYS,
  sortRulesByExecutionPriority,
  StageKey,
} from '../../utils/pipelineBucketing'

import { RuleCardRule } from './RuleCard'
import StageColumn from './StageColumn'

type StagePillProps = {
  title: string
  dotColorClass: string
  count: number
  isActive: boolean
  onClick: () => void
}

const StagePill = ({
  title,
  dotColorClass,
  count,
  isActive,
  onClick,
}: StagePillProps) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
      isActive
        ? 'border-doubleNickel-gray-400 bg-doubleNickel-gray-100 text-doubleNickel-gray-900'
        : 'border-doubleNickel-gray-200 bg-white text-doubleNickel-gray-600 hover:border-doubleNickel-gray-400 hover:text-doubleNickel-gray-900'
    }`}
  >
    <span
      className={`h-2 w-2 shrink-0 rounded-full ${dotColorClass}`}
      aria-hidden
    />
    <span>{title}</span>
    <span
      className={`rounded-full px-1.5 text-[10px] font-bold ${
        isActive
          ? 'bg-doubleNickel-gray-300 text-doubleNickel-gray-900'
          : 'bg-doubleNickel-gray-100 text-doubleNickel-gray-500'
      }`}
    >
      {count}
    </span>
  </button>
)

const STAGE_CONFIG: Record<
  StageKey,
  { title: string; dotColorClass: string }
> = {
  LEAD: { title: 'Lead', dotColorClass: 'bg-doubleNickel-gray-500' },
  APPLICATION: {
    title: 'Application',
    dotColorClass: 'bg-doubleNickel-brand-500',
  },
  QUALIFICATION: {
    title: 'Qualification',
    dotColorClass: 'bg-doubleNickel-warning-500',
  },
  ONBOARDING: {
    title: 'Onboarding',
    dotColorClass: 'bg-[#7C3AED]',
  },
  HIRED: {
    title: 'Hired',
    dotColorClass: 'bg-doubleNickel-success-500',
  },
  NOT_INTERESTED: {
    title: 'Not Interested',
    dotColorClass: 'bg-doubleNickel-warning-500',
  },
  DISQUALIFIED: {
    title: 'Disqualified',
    dotColorClass: 'bg-doubleNickel-error-500',
  },
  TERMINATED: {
    title: 'Terminated',
    dotColorClass: 'bg-doubleNickel-gray-500',
  },
  UNSCOPED: { title: 'Unscoped', dotColorClass: 'bg-doubleNickel-gray-300' },
}

type PipelineViewProps = {
  rules: RuleCardRule[]
  onRuleClick?: (rule: RuleCardRule) => void
}

const useToggleSet = <T,>(initial: Iterable<T>) => {
  const [set, setSet] = useState<Set<T>>(() => new Set(initial))
  const toggle = (key: T) =>
    setSet((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  return [set, toggle] as const
}

const PipelineView = ({ rules, onRuleClick }: PipelineViewProps) => {
  const rawBuckets = bucketRulesByStage(rules)
  const buckets = Object.fromEntries(
    Object.entries(rawBuckets).map(([key, list]) => [
      key,
      sortRulesByExecutionPriority(list),
    ])
  ) as typeof rawBuckets

  const [expandedOutcomes, toggleOutcome] = useToggleSet<StageKey>([])

  const expandedOutcomeList = OUTCOME_STAGE_KEYS.filter((s) =>
    expandedOutcomes.has(s)
  )

  return (
    // Viewport-bounded so the in-progress section can flex-fill without
    // depending on every ancestor (Mantine Tabs.Panel, DashboardLayout, etc.)
    // passing an explicit height down. The 200px reserves space for the page
    // header, SettingsPage tab strip, and RulesTab header bar.
    //
    // `grid-rows-1` on the in-progress grid is load-bearing: without an
    // explicit `1fr` row track, grid rows default to `auto` and grow with
    // their content — a column with many rules would then push the grid
    // taller than its bounded height and visually overlap the Outcomes
    // section, even though the column has its own `overflow-y-auto`.
    <div className="flex h-[calc(100vh-200px)] min-h-0 flex-col gap-6">
      {/* In-progress stages — always-visible 4-column grid */}
      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <span className="px-1 text-[11px] font-bold uppercase tracking-[0.06em] text-doubleNickel-gray-500">
          Pipeline
        </span>
        <div className="grid min-h-0 flex-1 grid-cols-4 grid-rows-1 gap-3">
          {MAIN_STAGE_KEYS.map((stage) => {
            const config = STAGE_CONFIG[stage]
            return (
              <StageColumn
                key={stage}
                title={config.title}
                dotColorClass={config.dotColorClass}
                rules={buckets[stage]}
                onRuleClick={onRuleClick}
              />
            )
          })}
        </div>
      </div>

      {/* Outcomes — pills + expanded lanes */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="px-1 text-[11px] font-bold uppercase tracking-[0.06em] text-doubleNickel-gray-500">
            Outcomes
          </span>
          {OUTCOME_STAGE_KEYS.map((stage) => {
            const config = STAGE_CONFIG[stage]
            return (
              <StagePill
                key={stage}
                title={config.title}
                dotColorClass={config.dotColorClass}
                count={buckets[stage].length}
                isActive={expandedOutcomes.has(stage)}
                onClick={() => toggleOutcome(stage)}
              />
            )
          })}
        </div>

        {expandedOutcomeList.length > 0 ? (
          <div
            className="grid max-h-[40vh] gap-3"
            style={{
              gridTemplateColumns: `repeat(${expandedOutcomeList.length}, minmax(0, 1fr))`,
            }}
          >
            {expandedOutcomeList.map((stage) => {
              const config = STAGE_CONFIG[stage]
              return (
                <StageColumn
                  key={stage}
                  title={config.title}
                  dotColorClass={config.dotColorClass}
                  rules={buckets[stage]}
                  onRuleClick={onRuleClick}
                />
              )
            })}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default PipelineView
